import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { format, isValid, parse } from "date-fns";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_WHITE,
} from "../Compliance/AuditHub/theme";
import { autocompleteSlotProps, fieldSx as defaultFieldSx } from "./formFieldStyles";
import {
  formTableBodyCellSx,
  formTableCardSx,
  formTableHeadCellSx,
} from "./formTableStyles";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const PROGRAM_TYPE_LABEL = {
  EDP: "EDP",
  PPA: "PPA",
  SPP: "SPP",
  RI_SP: "RI",
};

const GRID_COLUMNS = [
  { key: "name", label: "PROGRAM NAME" },
  { key: "type", label: "PROGRAM TYPE" },
  { key: "discount", label: "PROGRAM DISCOUNT" },
  { key: "endDate", label: "PROGRAM END DATE" },
];

const selectAllChipSx = (active) => ({
  height: 36,
  minHeight: 36,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
  cursor: "pointer",
  backgroundColor: active ? "#fff4eb" : AH_WHITE,
  border: `1px solid ${AH_ACCENT}`,
  color: AH_ACCENT,
  "&:hover": {
    backgroundColor: "#fff4eb",
    borderColor: AH_ACCENT_HOVER,
    color: AH_ACCENT_HOVER,
  },
  "&.Mui-disabled": {
    opacity: 0.5,
    borderColor: AH_BORDER,
    color: AH_MUTED,
    backgroundColor: AH_WHITE,
  },
});

const programKey = (program) => `${program?.program_type}:${program?.id}`;

const formatProgramDate = (value) => {
  if (!value) return "—";
  const parsed = parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : String(value);
};

/** Baseline / EDP discount from Partner Central program list API fields. */
const programDiscountFromApi = (program) => {
  if (!program || typeof program !== "object") return "—";
  const type = String(program.program_type || "");
  let raw;
  if (type === "EDP") raw = program.edp_discount_percent;
  else if (type === "SPP") raw = program.baseline_partner_discount_percent;
  else raw = program.baseline_distribution_discount_percent;

  if (raw == null || raw === "") {
    raw =
      program.baseline_distribution_discount_percent ??
      program.baseline_partner_discount_percent ??
      program.edp_discount_percent;
  }
  if (raw == null || raw === "") return "—";
  const amount = Number(String(raw).replace(/[%,\s]/g, ""));
  if (!Number.isFinite(amount)) return String(raw);
  return `${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
};

export const programOptionLabel = (program) => {
  const type = PROGRAM_TYPE_LABEL[program.program_type] || program.program_type || "";
  const name = program.program_display_name || program.agreement_id || program.id;
  return type ? `${name} (${type})` : String(name);
};

/**
 * Wholesale Programs multi-select (checkbox + Select All) with selected programs grid.
 * Grid columns are hydrated from listPartnerPrograms API records.
 */
const WholesaleProgramsField = ({
  id = "wholesalePrograms",
  options = [],
  value = [],
  onChange,
  loading = false,
  error = "",
  helperText = "",
  fieldError = "",
  fieldSx = defaultFieldSx,
}) => {
  const selectedPrograms = useMemo(() => {
    const keys = new Set(
      (value || []).map((ref) => `${ref.program_type}:${ref.program_id}`)
    );
    return (options || []).filter((program) => keys.has(programKey(program)));
  }, [options, value]);

  const allSelected =
    options.length > 0 &&
    selectedPrograms.length === options.length &&
    options.every((program) =>
      selectedPrograms.some(
        (selected) =>
          selected.id === program.id && selected.program_type === program.program_type
      )
    );

  const applySelection = (programs) => {
    onChange(
      programs.map((program) => ({
        program_type: program.program_type,
        program_id: program.id,
      }))
    );
  };

  const toggleSelectAll = () => {
    if (!options.length) return;
    if (allSelected) {
      onChange([]);
      return;
    }
    applySelection(options);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          component="label"
          htmlFor={id}
          sx={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: AH_TEXT,
            lineHeight: 1.4,
          }}
        >
          Wholesale Programs
        </Typography>
        <Typography sx={{ fontSize: 12, color: AH_MUTED, flexShrink: 0 }}>
          {selectedPrograms.length} selected
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 1,
        }}
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          id={id}
          options={options}
          value={selectedPrograms}
          loading={loading}
          onChange={(_event, next) => applySelection(next)}
          getOptionLabel={programOptionLabel}
          isOptionEqualToValue={(option, item) =>
            option.id === item.id && option.program_type === item.program_type
          }
          slotProps={autocompleteSlotProps}
          noOptionsText={
            loading ? "Loading programs…" : error || "No programs available"
          }
          sx={{ flex: 1, minWidth: 0 }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={{
                  display: "flex !important",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                  py: "6px !important",
                }}
              >
                <Checkbox
                  icon={checkboxIcon}
                  checkedIcon={checkboxCheckedIcon}
                  checked={selected}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    p: 0.25,
                    color: AH_MUTED,
                    "&.Mui-checked": { color: AH_ACCENT },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: AH_TEXT,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {programOptionLabel(option)}
                </Typography>
              </Box>
            );
          }}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search programs..."
              error={Boolean(fieldError)}
              helperText={fieldError || error || helperText}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={fieldSx}
            />
          )}
        />
        <Chip
          clickable
          disabled={!options.length}
          label="Select All"
          onClick={toggleSelectAll}
          aria-pressed={allSelected}
          aria-label={allSelected ? "Deselect all wholesale programs" : "Select All wholesale programs"}
          sx={selectAllChipSx(allSelected)}
        />
      </Box>

      {selectedPrograms.length ? (
        <TableContainer sx={{ ...formTableCardSx, mt: 1.5, overflowX: "auto" }}>
          <Table size="small" aria-label="Selected wholesale programs">
            <TableHead>
              <TableRow>
                {GRID_COLUMNS.map((column) => (
                  <TableCell key={column.key} sx={formTableHeadCellSx}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedPrograms.map((program) => (
                <TableRow key={programKey(program)} hover>
                  <TableCell
                    sx={{
                      ...formTableBodyCellSx,
                      fontWeight: 600,
                      color: AH_TEXT,
                      minWidth: 160,
                    }}
                  >
                    {program.program_display_name || program.agreement_id || program.id || "—"}
                  </TableCell>
                  <TableCell sx={formTableBodyCellSx}>
                    {PROGRAM_TYPE_LABEL[program.program_type] || program.program_type || "—"}
                  </TableCell>
                  <TableCell sx={formTableBodyCellSx}>
                    {programDiscountFromApi(program)}
                  </TableCell>
                  <TableCell sx={formTableBodyCellSx}>
                    {formatProgramDate(program.end_date)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
};

export default WholesaleProgramsField;
